import {useCallback, useEffect, useRef, useState} from 'react';
import {NavigateFunction} from 'react-router-dom';
import {getJobCrewMembers, setJobCrewClock, updateJobCrewMembers} from '../../job/services/crew-member.service';
import {AddOrUpdateJobCrewMemberDto, JobCrewMemberDto} from '../../job/dtos/job-crew-member.dto';
import {getEstimateTime} from '../estimate-time.service';
import RealTimeService from '../../../shared/services/real-time.service';
import {useAutosaveLanes} from './useAutosave';

/*
 * The job's crew clocks (SQ-597, SQ-559), as Android keeps them: each crew
 * member's clock runs on the server (`jobs/{id}/crew-members/{member}/clock`),
 * so every phone and browser sees it, and nothing this page does (leaving,
 * reloading, closing the tab) stops it; the server banks every clock when the
 * job is held, cancelled or finished, and runs the old job-length timer while
 * any of them runs. The page counts on from the server's reading with its own
 * clock and re-reads the crew when another device changes it. The pencil's
 * changes save after a quiet moment; on a running clock they are its new
 * reading (secondsAsOfNow) and it carries on from there.
 */

interface Run {
  /** Seconds on the clock at startedAt. */
  base: number;
  /** Date.now() when the clock read [base]. */
  startedAt: number;
}

/** What a member needs to be put on the job's crew: their role and the rate locked for them. */
export interface CrewMemberInfo {
  role?: string;
  hourlyRate?: number | null;
}

/** The server refuses a start dated back further than this (CrewClocks.MaxBackDateSeconds). */
const MAX_BACK_DATE_SECONDS = 14 * 24 * 60 * 60;

/** Before SQ-559 a clock ran in this browser only, from a start kept under this key. */
const legacyStorageKey = (workOrderId: number) => `savaquan.jobClocks.${workOrderId}`;

const readLegacyRuns = (workOrderId: number): Record<number, Run> => {
  try {
    const raw = window.localStorage.getItem(legacyStorageKey(workOrderId));
    return raw ? JSON.parse(raw) as Record<number, Run> : {};
  } catch {
    return {};
  }
};

const writeLegacyRuns = (workOrderId: number, runs: Record<number, Run>) => {
  try {
    if (Object.keys(runs).length === 0) window.localStorage.removeItem(legacyStorageKey(workOrderId));
    else window.localStorage.setItem(legacyStorageKey(workOrderId), JSON.stringify(runs));
  } catch {
    // Private windows refuse storage; there is nothing kept there to hand over then.
  }
};

const CREW_NOT_LOADED = "The job's crew time hasn't loaded. Reload the page before changing it.";

/** Rounded as the server banks a stop, so the readout never jumps when it answers. */
const elapsed = (run: Run, now: number) => run.base + Math.max(0, Math.round((now - run.startedAt) / 1000));

/** The clocks running on the server, as of a crew answer received at [now]. */
const serverRuns = (rows: JobCrewMemberDto[], now: number): Record<number, Run> => Object.fromEntries(rows
  .filter(m => m.runningSeconds !== null && m.runningSeconds !== undefined)
  .map(m => [m.companyCrewMemberId, {base: m.secondsWorked, startedAt: now - Math.max(0, m.runningSeconds ?? 0) * 1000}]));

const messageOf = (e: unknown, fallback: string) => e instanceof Error && e.message ? e.message : fallback;

export function useJobClocks(navigate: NavigateFunction, workOrderId: number | undefined, estimateId: number, onError: (message: string) => void) {
  const [jobCrew, setJobCrewRaw] = useState<JobCrewMemberDto[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [runs, setRunsRaw] = useState<Record<number, Run>>({});
  const [overlay, setOverlayRaw] = useState<Record<number, number>>({});
  const [, setTick] = useState(0);
  // The old job-length timer: a job from before per-crew clocks has its time only there.
  const [legacyJobSeconds, setLegacyJobSeconds] = useState(0);
  const crewRef = useRef(jobCrew);
  const runsRef = useRef(runs);
  const overlayRef = useRef(overlay);
  // Clocks this page changed that the server has not answered yet: a start or
  // stop in flight, or a pencil on a running clock waiting to save. Their run
  // here (null: stopped) wins over any crew read until then.
  const localRef = useRef<Record<number, Run | null>>({});
  // Whether the job's crew arrived: until it has, nobody's seconds are known, and a write would zero them.
  const loadRef = useRef<Promise<boolean>>(Promise.resolve(false));
  const loadedRef = useRef(false);
  const infoRef = useRef<Record<number, CrewMemberInfo>>({});
  const lanes = useAutosaveLanes(onError);
  const errorRef = useRef(onError);
  errorRef.current = onError;

  const setOverlay = (next: Record<number, number>) => { overlayRef.current = next; setOverlayRaw(next); };

  /** Shows the crew as the server answered it, except for clocks this page still owns. */
  const adopt = (rows: JobCrewMemberDto[]) => {
    crewRef.current = rows;
    setJobCrewRaw(rows);
    const next = serverRuns(rows, Date.now());
    Object.entries(localRef.current).forEach(([id, run]) => {
      if (run) next[Number(id)] = run;
      else delete next[Number(id)];
    });
    runsRef.current = next;
    setRunsRaw(next);
  };

  const setLocal = (id: number, run: Run | null) => {
    localRef.current = {...localRef.current, [id]: run};
    adopt(crewRef.current);
  };

  const clearLocal = (id: number) => {
    const next = {...localRef.current};
    delete next[id];
    localRef.current = next;
  };

  useEffect(() => {
    if (workOrderId === undefined) return undefined;
    let active = true;
    localRef.current = {};
    loadedRef.current = false;
    setLoaded(false);
    loadRef.current = getJobCrewMembers(navigate, workOrderId)
      .then(async rows => {
        if (!active) return false;
        adopt(rows);
        loadedRef.current = true;
        setLoaded(true);
        await handOverLegacyRuns(workOrderId, rows, () => active);
        return true;
      })
      // A failed load is not an empty crew: writes stay refused until the page is reloaded.
      .catch(() => false);
    getEstimateTime(navigate, estimateId)
      .then(t => { if (active) setLegacyJobSeconds(t && !t.followsCrewClocks ? t.elapsedSeconds : 0); })
      .catch(() => undefined);

    // Another phone or browser started, stopped or edited a clock: read the crew again.
    const refresh = () => {
      if (!active || !loadedRef.current) return;
      getJobCrewMembers(navigate, workOrderId).then(rows => { if (active) adopt(rows); }).catch(() => undefined);
    };
    RealTimeService.getInstance().onJobCrewChanged(id => { if (id === workOrderId) refresh(); });
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisible);
      RealTimeService.getInstance().onJobCrewChanged(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workOrderId, estimateId, navigate]);

  /**
   * A clock this browser was running before clocks moved to the server is
   * started there, dated back to when it began here, so its time is not
   * lost. One the server already runs, or one too old to hand over, is dropped.
   */
  const handOverLegacyRuns = async (jobId: number, rows: JobCrewMemberDto[], active: () => boolean) => {
    const legacy = readLegacyRuns(jobId);
    const left: Record<number, Run> = {};
    for (const [key, run] of Object.entries(legacy)) {
      const id = Number(key);
      const row = rows.find(m => m.companyCrewMemberId === id);
      const seconds = Math.round((Date.now() - run.startedAt) / 1000);
      if (!row || (row.runningSeconds !== null && row.runningSeconds !== undefined) || seconds < 0 || seconds > MAX_BACK_DATE_SECONDS) continue;
      try {
        const answer = await setJobCrewClock(navigate, jobId, id, true, seconds);
        if (active()) adopt(answer);
      } catch {
        left[id] = run;
      }
    }
    writeLegacyRuns(jobId, left);
  };

  const anyRunning = Object.keys(runs).length > 0;
  useEffect(() => {
    if (!anyRunning) return undefined;
    const timer = window.setInterval(() => setTick(t => t + 1), 1000);
    return () => window.clearInterval(timer);
  }, [anyRunning]);

  /** A member's seconds right now: a running clock to the second, else unsaved changes, else the server's. */
  const secondsOf = useCallback((id: number, now = Date.now()) => {
    const run = runsRef.current[id];
    if (run) return elapsed(run, now);
    return overlayRef.current[id] ?? crewRef.current.find(m => m.companyCrewMemberId === id)?.secondsWorked ?? 0;
  }, []);

  const isRunning = useCallback((id: number) => !!runsRef.current[id], []);

  const rowFor = (id: number, seconds: number, asOfNow: boolean): AddOrUpdateJobCrewMemberDto => {
    const member = crewRef.current.find(m => m.companyCrewMemberId === id);
    const info = infoRef.current[id];
    return {
      companyCrewMemberId: id, role: member?.role ?? info?.role ?? '', hourlyRate: member?.hourlyRate ?? info?.hourlyRate ?? null,
      secondsWorked: seconds, secondsAsOfNow: asOfNow,
    };
  };

  /**
   * Writes [ids]' seconds (adding any not on the job's crew yet); unsaved
   * changes made meanwhile stay. A running clock's seconds are its reading
   * now, and the server's clock carries on from them.
   */
  const persist = useCallback(async (ids: number[]) => {
    if (workOrderId === undefined || ids.length === 0) return;
    if (!(await loadRef.current)) throw new Error(CREW_NOT_LOADED);
    const onJob = (id: number) => crewRef.current.some(m => m.companyCrewMemberId === id);
    const sent = ids.map(id => rowFor(id, secondsOf(id), onJob(id) && isRunning(id)));
    const rows = await updateJobCrewMembers(navigate, workOrderId, {
      crewMembersToAdd: sent.filter(r => !onJob(r.companyCrewMemberId)),
      crewMembersToUpdate: sent.filter(r => onJob(r.companyCrewMemberId)),
      crewMemberIdsToRemove: [],
    });
    adopt(rows);
    const next = {...overlayRef.current};
    sent.forEach(r => { if (next[r.companyCrewMemberId] === r.secondsWorked) delete next[r.companyCrewMemberId]; });
    setOverlay(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, workOrderId, secondsOf, isRunning]);

  /** Remembers each member's role and locked rate, for when they join the job's crew. */
  const setMemberInfo = useCallback((info: Record<number, CrewMemberInfo>) => { infoRef.current = info; }, []);

  /** Start or stop a member's clock on the server. */
  const toggle = useCallback(async (id: number) => {
    if (workOrderId === undefined) return;
    if (!loadedRef.current) {
      errorRef.current(CREW_NOT_LOADED);
      return;
    }
    // A time still waiting to save goes first, sent as what it is (a running
    // clock's reading or a stopped one's time): the clock starts or stops from it.
    await lanes.flush();
    const start = !runsRef.current[id];
    const now = Date.now();
    // The readout moves at once; the server's answer replaces it.
    if (!start) setOverlay({...overlayRef.current, [id]: secondsOf(id, now)});
    setLocal(id, start ? {base: secondsOf(id, now), startedAt: now} : null);
    try {
      if (start && !crewRef.current.some(m => m.companyCrewMemberId === id)) await persist([id]);
      const rows = await setJobCrewClock(navigate, workOrderId, id, start);
      clearLocal(id);
      if (!start) {
        const next = {...overlayRef.current};
        delete next[id];
        setOverlay(next);
      }
      adopt(rows);
    } catch (e) {
      clearLocal(id);
      if (!start) {
        const next = {...overlayRef.current};
        delete next[id];
        setOverlay(next);
      }
      adopt(crewRef.current);
      errorRef.current(messageOf(e, start ? 'Failed to start the clock' : 'Failed to stop the clock'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, navigate, persist, secondsOf, workOrderId]);

  /** The pencil: a member's new time, saved after a quiet moment. On a running clock it is the clock's new reading. */
  const setSeconds = useCallback((id: number, seconds: number) => {
    if (!loadedRef.current) {
      errorRef.current(CREW_NOT_LOADED);
      return;
    }
    if (runsRef.current[id]) {
      const run = {base: Math.max(0, seconds), startedAt: Date.now()};
      setLocal(id, run);
      lanes.schedule(`crew-time-${id}`, async () => {
        try {
          await persist([id]);
        } finally {
          // A newer pencil change keeps the page's run until it saves.
          if (localRef.current[id] === run) {
            clearLocal(id);
            adopt(crewRef.current);
          }
        }
      });
      return;
    }
    setOverlay({...overlayRef.current, [id]: Math.max(0, seconds)});
    lanes.schedule(`crew-time-${id}`, () => persist([id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, persist]);

  /**
   * Stops every running clock and writes everything waiting (before review, a
   * hold, a cancel, finishing). The server banks every clock at a status
   * change too; stopping first puts the banked times on the page.
   */
  const stopAll = useCallback(async () => {
    await lanes.flush();
    if (workOrderId === undefined) return;
    for (const id of Object.keys(runsRef.current).map(Number)) {
      setLocal(id, null);
      try {
        const rows = await setJobCrewClock(navigate, workOrderId, id, false);
        clearLocal(id);
        adopt(rows);
      } catch (e) {
        clearLocal(id);
        adopt(crewRef.current);
        throw e;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, navigate, workOrderId]);

  /** Puts every selected member on the job's crew (Android's seedMissingJobCrewMembers). */
  const seedMissing = useCallback(async (selectedIds: number[]) => {
    if (!(await loadRef.current)) throw new Error(CREW_NOT_LOADED);
    const missing = selectedIds.filter(id => !crewRef.current.some(m => m.companyCrewMemberId === id));
    if (missing.length > 0) await persist(missing);
  }, [persist]);

  /** Takes a member, their time and their clock off the job. */
  const remove = useCallback(async (id: number) => {
    lanes.cancel(`crew-time-${id}`);
    clearLocal(id);
    const nextOverlay = {...overlayRef.current};
    delete nextOverlay[id];
    setOverlay(nextOverlay);
    if (workOrderId === undefined || !crewRef.current.some(m => m.companyCrewMemberId === id)) {
      adopt(crewRef.current);
      return;
    }
    adopt(await updateJobCrewMembers(navigate, workOrderId, {crewMembersToAdd: [], crewMembersToUpdate: [], crewMemberIdsToRemove: [id]}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lanes, navigate, workOrderId]);

  return {jobCrew, loaded, runs, legacyJobSeconds, secondsOf, isRunning, setMemberInfo, toggle, setSeconds, stopAll, seedMissing, remove, flush: lanes.flush};
}

export type JobClocks = ReturnType<typeof useJobClocks>;
